import { Card, CardContent, CardMedia, Grid, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { includes } from 'lodash';
import { OrderPageData } from '../api';
import { OrderPageTypography } from './themedComponents/OrderPageTypography';
import { calculatePrice } from './OrderUtils';
import { useLanguage } from '../common/GeneralUtils';
import MonkeybookWidgetFooter from '../common/ui/MonkeybookWidgetFooter';
import { getOrderPages } from '../common/AccountUtils';
import { parseArrayFromQueryString } from '../orders/orderPageQSSelectorUtil';
import { useAccountData } from './AccountSpecificThemeWrapper';
import { customerFacingPriceFormatter } from './utils';

export const VoucherSelectionWidget = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const language = useLanguage();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const account = useAccountData();
  const queryParams = new URLSearchParams(location.search);
  const iframeHeight = document.body.offsetHeight;
  const iframeWidth = document.body.offsetWidth;

  window.parent.postMessage({ height: iframeHeight, width: iframeWidth }, '*');
  const elementRef = useCallback((node: any) => {
    if (!node) return;
    const resizeObserver = new ResizeObserver(() => {
      const iframeHeight = document.body.offsetHeight;
      window.parent.postMessage({ height: iframeHeight }, '*');
    });
    resizeObserver.observe(node);
  }, []);

  const embeddedWidget = queryParams.get('embed') === 'true';
  const initialOrderPageIds = parseArrayFromQueryString(queryParams, 'orderpages');

  const orderPages =
    initialOrderPageIds.length > 0
      ? getOrderPages(account).filter((o: OrderPageData) => includes(initialOrderPageIds, o.id))
      : getOrderPages(account);

  const handleOrderPageClick = (orderPage: OrderPageData) => {
    navigate(`/order/${accountId}/${account?.experiences[0].id}/${orderPage.id}?vouchers=true`);
  };

  return (
    <Grid>
      <Grid
        mb={2}
        container
        display="flex"
        justifyContent="space-around"
        sx={{ '::-webkit-scrollbar': { display: 'none' } }}
        style={{
          height: '100%',
          width: '100%',
        }}>
        <Grid
          ref={elementRef}
          container
          flexDirection="column"
          style={{
            borderRadius: '12px',
            background: theme.customTheme.palette.background,
            height: '100%',
            width: '100%',
            maxWidth: '710px',
          }}>
          <Grid
            px={isSmallScreen ? undefined : 4}
            container
            flexDirection="column"
            sx={{
              color: theme.customTheme.palette.text.primary,
              border: `2px solid ${theme.customTheme.palette.border}`,
              borderRadius: '12px',
            }}
            boxShadow={4}>
            <Grid container mt={isSmallScreen ? 0 : 3} mb={1} p={4} pt={0} flexDirection="column" alignContent="center">
              {!isSmallScreen && !embeddedWidget && (
                <Grid container justifyContent="center" mb={4}>
                  <img width="250" src={account?.logo} alt="logo" />
                </Grid>
              )}
              <Grid container mb={2} justifyContent="center">
                <OrderPageTypography variant="h1" bold>
                  {t('multiplVouchers.title')}
                </OrderPageTypography>
              </Grid>
              <Grid container spacing={3} justifyContent="center">
                {orderPages.map((orderPage: OrderPageData) => {
                  const basePrice = calculatePrice(
                    orderPage,
                    {
                      duration: orderPage.duration,
                      rooms: 1,
                      persons: 1,
                      custom_details: {},
                      price: 0,
                    },
                    { date: new Date().toISOString() },
                  );
                  return (
                    <Grid item key={orderPage.id} xs={12} sm={6}>
                      <Card
                        onClick={() => handleOrderPageClick(orderPage)}
                        sx={{
                          cursor: 'pointer',
                          aspectRatio: isSmallScreen ? '1/1' : '2/3',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                          backgroundColor: theme.customTheme.palette.background,
                        }}>
                        {orderPage.photo && (
                          <CardMedia
                            component="img"
                            sx={{
                              height: isSmallScreen ? '70%' : '80%',
                              objectFit: 'cover',
                            }}
                            image={orderPage.photo}
                            alt={orderPage.label[language]}
                          />
                        )}
                        <CardContent
                          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <OrderPageTypography variant="h6" bold>
                            {orderPage.label[language]}
                          </OrderPageTypography>
                          <OrderPageTypography variant="body1">
                            {t('multiplVouchers.startingFrom', {
                              price: customerFacingPriceFormatter(basePrice, account!, true),
                            })}
                          </OrderPageTypography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <MonkeybookWidgetFooter languages={account?.customer_facing_widgets_languages} accountId={accountId} />
    </Grid>
  );
};
